import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  adoptAppVersion,
  appVersionsQuery,
  saveAppLicenses,
  type AppVersionsCard as Card,
  type VersionRelation,
} from '../../api/businessAppsVersions'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { failed } from '../parts'
import './versions.css'

const RELATION: Record<VersionRelation, { label: string; pill: string }> = {
  same: { label: 'same as server', pill: 'adm-pill-ok' },
  older: { label: 'older', pill: 'adm-pill-warn' },
  newer: { label: 'newer', pill: 'adm-pill-warn' },
  other: { label: 'different', pill: 'adm-pill-warn' },
  unknown: { label: 'version not reported', pill: '' },
  none: { label: '', pill: '' },
}

/**
 * "Versions & licenses" pe fisa aplicatiei.
 *
 * Versiunea de pe server si distributia versiunilor pe statiile cu clientul,
 * luate din software-ul instalat dupa regulile aplicatiei din catalog. Ce se
 * gaseste NU se scrie singur in fisa: "Use detected version" o face, la clic.
 *
 * Licentele: locurile si tipul (scrise de om) langa numarul de statii pe care
 * e instalat clientul - toate masinile firmei, nu doar componentele adaugate.
 *
 * Fara dreptul de citire sau inainte de migrare, cardul nu apare.
 */
export function AppVersionsCard({ appId }: { appId: number }) {
  const query = useQueryClient()
  const page = useQuery({ ...appVersionsQuery(appId), retry: false })
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)

  const data = page.data
  if (!data?.ready || !Array.isArray(data.distribution) || !data.licenses) return null

  const adopt = () => {
    setBusy(true)
    adoptAppVersion(appId)
      .then((r) => {
        toast(r.message, 'Applications', r.status === 'OK' ? 'success' : 'error')
        if (r.status === 'OK') void query.invalidateQueries({ queryKey: ['business-apps'] })
      })
      .catch((e) => toast(failed(e), 'Applications', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <>
      <div className="adm-section">
        <h4>Versions &amp; licenses</h4>
        <span className="adm-section-note">from the software installed on the customer&apos;s machines</span>
      </div>
      <div className="adm-card" id="appVersions">
        {data.catalog === null ? (
          <p className="text-muted av-pad">
            This application is not in the application catalog, so there is no rule saying which installed program is its
            server or its client. Add it to the catalog (Applications &gt; Suggested &gt; Catalog) to see versions and installations.
          </p>
        ) : (
          <>
            <VersionsBlock data={data} busy={busy} onAdopt={adopt} />
            <Distribution data={data} />
          </>
        )}
        <LicensesBlock data={data} onEdit={() => setEditing(true)} />
        {data.signals.length > 0 && (
          <ul className="av-signals" id="appVersionSignals">
            {data.signals.map((s) => (
              <li key={s.code} className={`av-signal av-signal-${s.level}`}>
                <i className={s.level === 'info' ? 'fas fa-info-circle' : 'fas fa-exclamation-triangle'}></i> {s.text}
              </li>
            ))}
          </ul>
        )}
      </div>
      {editing && <LicensesModal data={data} onClose={() => setEditing(false)} />}
    </>
  )
}

function VersionsBlock({ data, busy, onAdopt }: { data: Card; busy: boolean; onAdopt(): void }) {
  return (
    <div className="av-row">
      <div className="adm-field">
        <label>On the server</label>
        <div id="appServerVersion">
          {data.serverVersion !== null ? (
            <strong>{data.serverVersion}</strong>
          ) : (
            <span className="adm-muted">{data.hasServerRules ? 'not found' : 'no server program in the catalog rules'}</span>
          )}
          {data.servers.length > 0 && (
            <div className="adm-muted small">
              {data.servers.map((s) => `${s.device}${s.component ? '' : ' (not a component)'}`).join(', ')}
            </div>
          )}
        </div>
      </div>
      <div className="adm-field">
        <label>Recorded</label>
        <div>
          {data.recorded !== '' ? data.recorded : <span className="adm-muted">not filled in</span>}
          {data.canEdit && data.canAdopt && (
            <div>
              <button type="button" id="appAdoptVersion" className="btn btn-default btn-xs" disabled={busy} onClick={onAdopt}>
                <i className="fas fa-check"></i> Use detected version {data.serverVersion}
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="adm-field">
        <label>Workstations with the client</label>
        <div id="appWorkstations">
          {data.workstations === null ? (
            <span className="adm-muted">no client program in the catalog rules</span>
          ) : (
            <>
              <strong>{data.workstations}</strong>
              {data.clientSoftware !== '' && <span className="adm-muted small"> &middot; {data.clientSoftware}</span>}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function Distribution({ data }: { data: Card }) {
  if (data.distribution.length === 0) return null
  return (
    <table className="table adm-list" id="appVersionDistribution">
      <thead>
        <tr>
          <th>Client version</th>
          <th className="text-center">Workstations</th>
          <th>Some of them</th>
        </tr>
      </thead>
      <tbody>
        {data.distribution.map((g) => {
          const rel = RELATION[g.relation]
          return (
            <tr key={g.version === '' ? '-' : g.version}>
              <td>
                {g.version === '' ? <span className="adm-muted">unknown</span> : g.version}{' '}
                {rel.label !== '' && data.reference !== null && g.version !== '' && (
                  <span className={`adm-pill ${rel.pill}`}>{g.relation === 'same' && data.referenceFrom === 'recorded' ? 'same as recorded' : rel.label}</span>
                )}
                {g.version === '' && <span className="adm-muted small"> {rel.label}</span>}
              </td>
              <td className="text-center">{g.count}</td>
              <td className="adm-muted small">
                {g.devices.join(', ')}
                {g.count > g.devices.length ? ` and ${g.count - g.devices.length} more` : ''}
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

function LicensesBlock({ data, onEdit }: { data: Card; onEdit(): void }) {
  const l = data.licenses
  return (
    <div className="av-row av-licenses" id="appLicenses">
      <div className="adm-field">
        <label>Licensed seats</label>
        <div>
          {l.seats === null ? (
            <span className="adm-muted">not filled in</span>
          ) : (
            <>
              <strong>{l.seats}</strong> <span className="adm-muted small">{l.typeLabel.toLowerCase()}</span>
            </>
          )}
        </div>
      </div>
      <div className="adm-field">
        <label>Installed</label>
        <div>{l.installed === null ? <span className="adm-muted">unknown</span> : l.installed}</div>
      </div>
      <div className="adm-field">
        <label>Licenses</label>
        <div>
          {l.state === 'over' && <span className="adm-pill adm-pill-bad">{l.excess} over</span>}
          {l.state === 'unused' && <span className="adm-pill adm-pill-warn">{l.unused} unused</span>}
          {l.state === 'ok' && <span className="adm-pill adm-pill-ok">covered</span>}
          {(l.state === 'unset' || l.state === 'unknown') && <span className="adm-muted">-</span>}
        </div>
      </div>
      {data.canEdit && l.ready && (
        <div className="ci-actions">
          <button type="button" id="appLicensesEdit" className="btn btn-default btn-xs" onClick={onEdit}>
            <i className="fa fa-pencil-alt"></i> Licenses
          </button>
        </div>
      )}
      {!l.ready && <span className="adm-muted small">Licenses can be recorded after the database migration runs.</span>}
    </div>
  )
}

function LicensesModal({ data, onClose }: { data: Card; onClose(): void }) {
  const query = useQueryClient()
  const [seats, setSeats] = useState(data.licenses.seats === null ? '' : String(data.licenses.seats))
  const [type, setType] = useState(data.licenses.type)
  const [busy, setBusy] = useState(false)

  const save = () => {
    setBusy(true)
    saveAppLicenses(data.appId, seats.trim(), type)
      .then((r) => {
        toast(r.message, 'Applications', r.status === 'OK' ? 'success' : 'error')
        if (r.status === 'OK') {
          void query.invalidateQueries({ queryKey: ['business-apps'] })
          onClose()
        }
      })
      .catch((e) => toast(failed(e), 'Applications', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <Modal
      id={`appLicenses-${data.appId}`}
      open
      onClose={onClose}
      title="Licenses"
      footer={
        <>
          <button type="button" className="btn btn-default" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" id="appLicensesSave" disabled={busy} onClick={save}>
            Save
          </button>
        </>
      }
    >
      <div className="form-group">
        <label htmlFor="avSeats">Licensed seats</label>
        <input
          id="avSeats"
          className="form-control"
          inputMode="numeric"
          placeholder="e.g. 40"
          value={seats}
          maxLength={7}
          onChange={(ev) => setSeats(ev.target.value)}
        />
        <p className="help-block">Leave empty when the number of licenses is not known.</p>
      </div>
      <div className="form-group">
        <label htmlFor="avType">License type</label>
        <select id="avType" className="form-control" value={type} onChange={(ev) => setType(ev.target.value)}>
          {data.licenseTypes.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        <p className="help-block">
          Installed now on {data.licenses.installed ?? 'an unknown number of'} workstation{data.licenses.installed === 1 ? '' : 's'}. With
          concurrent licenses, more installations than seats is normal.
        </p>
      </div>
    </Modal>
  )
}
